import type { QueryClient } from "@tanstack/react-query";
import { reconcileMealReads } from "./invalidate-after-log";
import { apiFetch, ApiFetchError } from "./api";

export const MEAL_RECONCILIATION_INTERVAL_MS = 60_000;
export const MEAL_RECONCILIATION_RECOVERY_MS = 5 * 60_000;
export const MEAL_REVISION_TIMEOUT_MS = 8_000;

/**
 * One reconciler per authenticated account lifecycle. No cross-tab storage,
 * identifiers, meal contents or messages: all reads use normal API auth.
 * Background/offline tabs pause; returning tabs reconcile immediately.
 */
export function startMealReconciliation(qc: QueryClient): () => void {
  let stopped = false;
  let pending = false;
  let lastStarted = -Infinity;
  let lastRefreshed = -Infinity;
  let revision: string | undefined;
  let controller: AbortController | undefined;
  const check = async () => {
    controller = new AbortController();
    const timeout = window.setTimeout(() => controller?.abort(), MEAL_REVISION_TIMEOUT_MS);
    let next: string | undefined;
    try {
      const result = await apiFetch<{ revision: string }>("/logs/revision", {
        signal: controller.signal, cache: "no-store",
      });
      if (!/^[a-f0-9]{32}$/.test(result.revision)) throw new Error("Invalid meal revision");
      next = result.revision;
    } catch (error) {
      // An expired/revoked session is not permission for a fan-out of reads.
      if (error instanceof ApiFetchError && (error.status === 401 || error.status === 403)) return;
      // Unsupported endpoint, timeout, or network failure: retain the original
      // bounded recovery path rather than trust an absent signal.
    } finally {
      window.clearTimeout(timeout);
    }
    if (stopped || document.visibilityState === "hidden" || !navigator.onLine) return;
    if (next === undefined || next !== revision ||
        Date.now() - lastRefreshed >= MEAL_RECONCILIATION_RECOVERY_MS) {
      await reconcileMealReads(qc);
      if (stopped) return;
      // Only acknowledge after successful reads. Failure must retry even when
      // the revision is unchanged. Never fetch a new token AFTER the reads:
      // that could acknowledge a write which the reads didn't observe.
      revision = next;
      lastRefreshed = Date.now();
    }
  };
  const reconcile = () => {
    if (stopped || pending || document.visibilityState === "hidden" || !navigator.onLine) return;
    // Coalesce visibility/focus/online events without resetting the interval.
    if (Date.now() - lastStarted < 5_000) return;
    lastStarted = Date.now();
    pending = true;
    void check().catch(() => {
      // Query errors retain their cached data. The next bounded read retries.
    }).finally(() => { pending = false; });
  };
  const timer = window.setInterval(reconcile, MEAL_RECONCILIATION_INTERVAL_MS);
  window.addEventListener("focus", reconcile);
  window.addEventListener("online", reconcile);
  document.addEventListener("visibilitychange", reconcile);
  return () => {
    stopped = true;
    controller?.abort();
    window.clearInterval(timer);
    window.removeEventListener("focus", reconcile);
    window.removeEventListener("online", reconcile);
    document.removeEventListener("visibilitychange", reconcile);
  };
}